import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddCustomerDialog } from "@/components/add-customer-dialog";
import { AddJobDialog } from "@/components/add-job-dialog";
import { CustomerTable } from "@/components/customer-table";
import { CustomerProfilePage } from "@/components/customer-profile-page";
import { PaymentsTable } from "@/components/payments-table";
import { OverdueTable } from "@/components/overdue-table";
import { SettingsPage } from "@/components/settings-page";
import { ReportsPage } from "@/components/reports-page";
import { ExpensesPage } from "@/components/expenses-page";
import { DashboardPage } from "@/components/dashboard-page";
import { Pagination } from "@/components/ui/pagination";

import { Expense } from "@/types/expense";
import { UsersPage, UsersPageRef } from "@/components/users-page";
import { MultiSaleDialog } from "@/components/multi-sale-dialog";
import { EditSaleDialog } from "@/components/edit-sale-dialog";
import { CustomerPaymentSheet } from "@/components/customer-payment-sheet";

import { LogoutDialog } from "@/components/ui/confirmation-dialog";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { Search, Users, UserCog, UserPlus, Smartphone, Banknote, DollarSign, Package, ArrowLeft, ArrowRight, Receipt, FileBarChart, Settings, LogOut, LayoutDashboard, Bell, ShoppingCart, CheckCircle, CreditCard, Menu, X, Shield, Trash2, MoreHorizontal, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PWAInstallButton } from "@/components/pwa-install-button";
import { useAuth } from "@/contexts/auth-context";
import { useDataService } from "@/hooks/use-data-service";
import { RealtimeStatus } from "@/components/realtime-status";

import { useToast } from "@/contexts/toast-context";
import { useConfirm } from "@/contexts/confirm-context";

interface AuthenticatedAppProps {
  onLogout: () => void;
}

export function AuthenticatedApp({ onLogout }: AuthenticatedAppProps) {
  const { success, error: showError } = useToast();
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const {
    customers,
    products,
    expenses,
    jobs,
    isLoading: dataLoading,
    isRealtimeConnected,
    isRealtimeEnabled,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addProduct,
    updateProduct,
    deleteProduct,
    updatePayment,
    handlePayment,
    addExpense,
    updateExpense,
    deleteExpense,
    addJob,
    deleteJob,
  } = useDataService();

  const [searchTerm, setSearchTerm] = useState("");
  const [activePage, setActivePage] = useState<"dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "users" | "settings" | "customer-profile">("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paymentsSelectedCustomer, setPaymentsSelectedCustomer] = useState<Customer | null>(null);
  const [paymentsProfileOpen, setPaymentsProfileOpen] = useState(false);
  const [selectedCustomerForProfile, setSelectedCustomerForProfile] = useState<Customer | null>(null);


  // Users page ref
  const usersPageRef = useRef<UsersPageRef>(null);

  // Sales page state
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());
  const [salesSearchTerm, setSalesSearchTerm] = useState("");
  const [editingSale, setEditingSale] = useState<Product | null>(null);
  const [editSaleDialogOpen, setEditSaleDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  
  // Pagination for sales tabs
  const [activeSalesPage, setActiveSalesPage] = useState(1);
  const [completedSalesPage, setCompletedSalesPage] = useState(1);
  const salesItemsPerPage = 100;

  // Main content ref for scroll reset
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Scroll to top when page changes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  }, [activePage]);

  // Expenses page state
  const [addExpenseDialogOpen, setAddExpenseDialogOpen] = useState(false);

  const toggleSaleExpanded = (saleId: string) => {
    setExpandedSales(prev => {
      const newSet = new Set<string>();
      if (!prev.has(saleId)) {
        newSet.add(saleId);
      }
      return newSet;
    });
  };

  // Apply saved theme and color on load
  useEffect(() => {
    const root = document.documentElement;

    // Load theme and color from localStorage (separate keys)
    const theme = localStorage.getItem("qandil-theme") || "system";
    let primaryColor = localStorage.getItem("qandil-color") || "blue";

    // Apply theme
    const isDark = theme === "dark" ||
      (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

    if (theme === "dark") {
      root.classList.add("dark");
    } else if (theme === "light") {
      root.classList.remove("dark");
    } else {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    }

    // Apply primary color
    const COLORS: Record<string, { light: string; dark: string }> = {
      blue: { light: "221.2 83.2% 53.3%", dark: "217.2 91.2% 59.8%" },
      green: { light: "142.1 76.2% 36.3%", dark: "142.1 70.6% 45.3%" },
      red: { light: "0 72.2% 50.6%", dark: "0 72.2% 50.6%" },
      purple: { light: "262.1 83.3% 57.8%", dark: "263.4 70% 50.4%" },
      orange: { light: "24.6 95% 53.1%", dark: "20.5 90.2% 48.2%" },
    };

    // Validate color - if invalid (like white/black), reset to blue
    if (!COLORS[primaryColor]) {
      primaryColor = "blue";
      localStorage.setItem("qandil-color", "blue");
    }

    const colorData = COLORS[primaryColor] || COLORS.blue;
    if (colorData) {
      const hsl = isDark ? colorData.dark : colorData.light;
      root.style.setProperty("--primary", `hsl(${hsl})`);
      if (isDark) {
        root.style.setProperty("--primary-foreground", "hsl(0 0% 9%)");
      } else {
        root.style.setProperty("--primary-foreground", "hsl(0 0% 98%)");
      }
    }
  }, []);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

  const handleAddCustomer = async (customerData: Omit<Customer, "id" | "createdAt">) => {
    try {
      await addCustomer(customerData);
      success(`کڕیار بە سەرکەوتوویی زیادکرا: ${customerData.fullName}`);
    } catch (err) {
      console.error("Failed to add customer:", err);
      showError("هەڵەیەک ڕوویدا لە زیادکردنی کڕیار");
    }
  };

  const handleAddJob = async (job: string) => {
    try {
      await addJob(job);
      success(`پیشە زیادکرا: ${job}`);
    } catch (err) {
      console.error("Failed to add job:", err);
      showError("هەڵەیەک ڕوویدا لە زیادکردنی پیشە");
    }
  };

  const handleDeleteJob = async (job: string) => {
    try {
      await deleteJob(job);
      success(`پیشە سڕایەوە: ${job}`);
    } catch (err) {
      console.error("Failed to delete job:", err);
      showError("هەڵەیەک ڕوویدا لە سڕینەوەی پیشە");
    }
  };

  const handleAddProduct = async (productData: Omit<Product, "id" | "createdAt" | "remainingAmount">) => {
    try {
      await addProduct(productData);
      // success message لە MultiSaleDialog نیشان دەدرێت بۆ ئەوەی دووبارە نەبێتەوە
    } catch (err) {
      console.error("Failed to add product:", err);
      showError("هەڵەیەک ڕوویدا لە زیادکردنی فرۆشتن");
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await deleteProduct(id);
      success("فرۆشتن سڕایەوە");
    } catch (err) {
      console.error("Failed to delete product:", err);
      showError("هەڵەیەک ڕوویدا لە سڕینەوەی فرۆشتن");
    }
  };

  const handleUpdatePayment = async (id: string, paidAmount: number) => {
    try {
      await updatePayment(id, paidAmount);
      success(`پارەدان نوێکرایەوە: ${paidAmount.toLocaleString()} دینار`);
    } catch (err) {
      console.error("Failed to update payment:", err);
      showError("هەڵەیەک ڕوویدا لە نوێکردنەوەی پارەدان");
    }
  };

  const handleUpdateProduct = async (updatedProduct: Product) => {
    try {
      await updateProduct(updatedProduct);
      success(`فرۆشتن نوێکرایەوە: ${updatedProduct.deviceName}`);
    } catch (err) {
      console.error("Failed to update product:", err);
      showError("هەڵەیەک ڕوویدا لە نوێکردنەوەی فرۆشتن");
    }
  };

  const handleUpdateCustomer = async (updatedCustomer: Customer) => {
    try {
      await updateCustomer(updatedCustomer);
      success(`کڕیار نوێکرایەوە: ${updatedCustomer.fullName}`);
    } catch (err) {
      console.error("Failed to update customer:", err);
      showError("هەڵەیەک ڕوویدا لە نوێکردنەوەی کڕیار");
    }
  };

  const handlePaymentAction = async (productId: string, amount: number) => {
    try {
      await handlePayment(productId, amount);
      success(`پارەدان تۆمارکرا: ${amount.toLocaleString()} دینار`);
    } catch (err) {
      console.error("Failed to process payment:", err);
      showError("هەڵەیەک ڕوویدا لە پارەدان");
    }
  };

  const customerCountByJob = customers.reduce((acc, customer) => {
    if (customer.job) {
      acc[customer.job] = (acc[customer.job] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const handleDeleteCustomer = async (id: string) => {
    try {
      await deleteCustomer(id);
      success("کڕیار سڕایەوە");
    } catch (err) {
      console.error("Failed to delete customer:", err);
      showError("هەڵەیەک ڕوویدا لە سڕینەوەی کڕیار");
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const search = searchTerm.toLowerCase();
    return (
      (customer.fullName || '').toLowerCase().includes(search) ||
      (customer.phone1 || '').includes(search) ||
      (customer.phone2 || '').includes(search) ||
      (customer.guarantorName || '').toLowerCase().includes(search) ||
      (customer.guarantorPhone || '').includes(search) ||
      (customer.job || '').toLowerCase().includes(search) ||
      (customer.address || '').toLowerCase().includes(search) ||
      (customer.customerCode || '').toString().includes(search)
    );
  });

  const handleViewProfile = (customer: Customer) => {
    setSelectedCustomerForProfile(customer);
    setActivePage("customer-profile");
  };

  const handleBackFromProfile = () => {
    setActivePage("customers");
    setSelectedCustomerForProfile(null);
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">چاوەڕوان بە...</div>
      </div>
    );
  }

  const existingCodes = customers.map(c => c.customerCode);

  // Calculate totals for IQD
  const iqdProducts = products.filter(p => p.currency === "IQD");
  const totalSaleIQD = iqdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidIQD = iqdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingIQD = iqdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);

  // Calculate totals for USD
  const usdProducts = products.filter(p => p.currency === "USD");
  const totalSaleUSD = usdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidUSD = usdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingUSD = usdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);

  return (
    <div className="h-screen bg-background flex flex-col md:flex-row overflow-hidden" dir="rtl">

      {/* Mobile Header */}
      <header className="md:hidden border-b bg-background flex-shrink-0">
        <div className="flex items-center h-14 px-4 w-full">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-bold text-base">
              {activePage === "dashboard" && "داشبۆرد"}
              {activePage === "customers" && "کڕیارەکان"}
              {activePage === "sales" && "فرۆشتنەکان"}
              {activePage === "payments" && "پارەدان"}
              {activePage === "expenses" && "خەرجییەکان"}
              {activePage === "notifications" && "دواکەوتووەکان"}
              {activePage === "reports" && "ڕاپۆرتەکان"}
              {activePage === "users" && "بەکارهێنەران"}
              {activePage === "settings" && "ڕێکخستن"}
            </span>
          </div>
          <div className="flex-1"></div>
          <div className="flex items-center gap-1">
            {activePage === "customers" && (
              <>
                <AddCustomerDialog onAdd={handleAddCustomer} existingCodes={existingCodes} jobs={jobs} />
                <AddJobDialog jobs={jobs} onAddJob={handleAddJob} onDeleteJob={handleDeleteJob} customerCountByJob={customerCountByJob} />
              </>
            )}
            {activePage === "sales" && (
              <MultiSaleDialog customers={customers} products={products} onAdd={handleAddProduct} />
            )}
            {activePage === "expenses" && (
              <Button
                size="sm"
                className="flex items-center gap-2 text-xs px-3 py-2"
                onClick={() => setAddExpenseDialogOpen(true)}
              >
                <Receipt className="h-3 w-3" />
                زیادکردنی خەرجی
              </Button>
            )}
            {activePage === "users" && (
              <Button size="sm" className="flex items-center gap-2 text-xs px-3 py-2" onClick={() => usersPageRef.current?.openAddDialog()}>
                <UserPlus className="h-3 w-3" />
                زیادکردنی بەکارهێنەر
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 top-14 z-40 transition-all duration-300 ease-out ${mobileMenuOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
          }`}
        onClick={(e) => {
          // Close menu when clicking on overlay background
          if (e.target === e.currentTarget) {
            setMobileMenuOpen(false);
          }
        }}
        onTouchMove={(e) => {
          // Prevent scrolling on touch devices
          if (mobileMenuOpen) e.preventDefault();
        }}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'
            }`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-card border-l shadow-2xl transform transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* User Info Header in Mobile Menu */}
          <div className="h-20 px-4 border-b bg-gradient-to-r from-primary/5 to-primary/10">
            <div className="flex items-center gap-3 h-full">
              <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center shadow-lg ring-2 ring-primary/20">
                <span className="text-primary-foreground font-bold text-lg">
                  {user?.fullName?.charAt(0)?.toUpperCase() || 'ق'}
                </span>
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-bold text-sm truncate">{user?.fullName || 'بەکارهێنەر'}</span>
                <span className="text-xs text-muted-foreground truncate">{user?.email || ''}</span>
                <span className="text-xs font-medium text-primary">
                  {user?.role === 'admin' ? 'بەڕێوەبەر' : 'بەکارهێنەر'}
                </span>
              </div>
            </div>
          </div>
          <nav className="p-4 space-y-2">
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "dashboard" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("dashboard"); setMobileMenuOpen(false); }}
            >
              <LayoutDashboard className="h-5 w-5" />
              داشبۆرد
            </button>
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "customers" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("customers"); setMobileMenuOpen(false); }}
            >
              <Users className="h-5 w-5" />
              کڕیارەکان
            </button>
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "sales" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("sales"); setMobileMenuOpen(false); }}
            >
              <ShoppingCart className="h-5 w-5" />
              فرۆشتنەکان
            </button>
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "payments" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("payments"); setMobileMenuOpen(false); }}
            >
              <CreditCard className="h-5 w-5" />
              پارەدان
            </button>
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "expenses" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("expenses"); setMobileMenuOpen(false); }}
            >
              <Receipt className="h-5 w-5" />
              خەرجییەکان
            </button>
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "notifications" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("notifications"); setMobileMenuOpen(false); }}
            >
              <Bell className="h-5 w-5" />
              دواکەوتووەکان
            </button>
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "reports" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("reports"); setMobileMenuOpen(false); }}
            >
              <FileBarChart className="h-5 w-5" />
              ڕاپۆرتەکان
            </button>
            <div className="border-t my-2" />
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "users" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("users"); setMobileMenuOpen(false); }}
            >
              <UserCog className="h-5 w-5" />
              بەکارهێنەران
            </button>
            <button
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "settings" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("settings"); setMobileMenuOpen(false); }}
            >
              <Settings className="h-5 w-5" />
              ڕێکخستن
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-all duration-200" onClick={() => { setLogoutDialogOpen(true); setMobileMenuOpen(false); }}>
              <LogOut className="h-5 w-5" />
              چوونەدەرەوە
            </button>
          </nav>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-56 border-l bg-white dark:bg-muted/30 flex-col h-screen sticky top-0">
        {/* User Info Header */}
        <div className="h-20 px-4 border-b bg-gradient-to-r from-primary/5 to-primary/10">
          <div className="flex items-center gap-3 h-full">
            <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center shadow-lg ring-2 ring-primary/20">
              <span className="text-primary-foreground font-bold text-base">
                {user?.fullName?.charAt(0)?.toUpperCase() || 'ق'}
              </span>
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="font-bold text-sm truncate">{user?.fullName || 'بەکارهێنەر'}</span>
              <span className="text-[10px] text-muted-foreground truncate leading-tight">{user?.email || ''}</span>
              <span className="text-xs font-medium text-primary">
                {user?.role === 'admin' ? 'بەڕێوەبەر' : 'بەکارهێنەر'}
              </span>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "dashboard" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("dashboard")}
          >
            <LayoutDashboard className="h-4 w-4" />
            داشبۆرد
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "customers" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("customers")}
          >
            <Users className="h-4 w-4" />
            کڕیارەکان
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "sales" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("sales")}
          >
            <ShoppingCart className="h-4 w-4" />
            فرۆشتنەکان
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "payments" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("payments")}
          >
            <CreditCard className="h-4 w-4" />
            پارەدان
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "expenses" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("expenses")}
          >
            <Receipt className="h-4 w-4" />
            خەرجییەکان
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "notifications" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("notifications")}
          >
            <Bell className="h-4 w-4" />
            دواکەوتووەکان
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "reports" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("reports")}
          >
            <FileBarChart className="h-4 w-4" />
            ڕاپۆرتەکان
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "users" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("users")}
          >
            <UserCog className="h-4 w-4" />
            بەکارهێنەران
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "settings" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("settings")}
          >
            <Settings className="h-4 w-4" />
            ڕێکخستن
          </button>
        </nav>
        <div className="p-3 border-t">
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer" onClick={() => setLogoutDialogOpen(true)}>
            <LogOut className="h-4 w-4" />
            چوونەدەرەوە
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Desktop Header */}
        <header className="hidden md:block border-b bg-background flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4 min-w-0 flex-shrink">
              <span className="font-semibold truncate">
                {activePage === "dashboard" && "داشبۆرد"}
                {activePage === "customer-profile" && "پرۆفایلی کڕیار"}
                {activePage === "customers" && (
                  <span className="flex items-center gap-3">
                    کڕیارەکان
                    <Badge variant="secondary" className="text-xs">
                      {customers.length} کڕیار تۆمارکراو
                    </Badge>
                  </span>
                )}
                {activePage === "payments" && (
                  <span className="flex items-center gap-3">
                    پارەدان
                    <Badge variant="secondary" className="text-xs">
                      {products.filter(p => p.remainingAmount > 0).length} قیست ماوە
                    </Badge>
                  </span>
                )}
                {activePage === "sales" && (
                  <span className="flex items-center gap-3">
                    فرۆشتنەکان
                    <Badge variant="secondary" className="text-xs">
                      {products.length} فرۆشتن
                    </Badge>
                  </span>
                )}
                {activePage === "expenses" && "خەرجییەکان"}
                {activePage === "notifications" && "دواکەوتووەکان"}
                {activePage === "reports" && "ڕاپۆرتەکان"}
                {activePage === "users" && "بەکارهێنەران"}
                {activePage === "settings" && "ڕێکخستن"}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <RealtimeStatus isConnected={isRealtimeConnected} isEnabled={isRealtimeEnabled} />
              <PWAInstallButton variant="outline" size="sm" />
              {activePage === "customer-profile" && (
                <Button variant="outline" onClick={handleBackFromProfile}>
                  گەڕانەوە
                  <ArrowLeft className="h-4 w-4 mr-2" />
                </Button>
              )}
              {activePage === "customers" && (
                <>
                  <AddCustomerDialog onAdd={handleAddCustomer} existingCodes={existingCodes} jobs={jobs} />
                  <AddJobDialog jobs={jobs} onAddJob={handleAddJob} onDeleteJob={handleDeleteJob} customerCountByJob={customerCountByJob} />
                </>
              )}
              {activePage === "sales" && (
                <MultiSaleDialog customers={customers} products={products} onAdd={handleAddProduct} />
              )}
              {activePage === "expenses" && (
                <Button
                  size="sm"
                  className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 md:px-4 md:py-2"
                  onClick={() => setAddExpenseDialogOpen(true)}
                >
                  <Receipt className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">زیادکردنی خەرجی نوێ</span>
                  <span className="sm:hidden">زیادکردن</span>
                </Button>
              )}
              {activePage === "users" && (
                <Button size="sm" className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 md:px-4 md:py-2" onClick={() => usersPageRef.current?.openAddDialog()}>
                  <UserPlus className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">زیادکردنی بەکارهێنەر</span>
                  <span className="sm:hidden">بەکارهێنەر</span>
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main
          ref={mainContentRef}
          className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4"
        >
          {activePage === "dashboard" && (
            <DashboardPage
              customers={customers}
              products={products}
              expenses={expenses}
              onNavigate={(page) => setActivePage(page)}
            />
          )}

          {activePage === "customers" && (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Total Customers */}
                <div className="rounded-xl border bg-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold">{customers.length}</p>
                  <p className="text-xs text-muted-foreground">کۆی کڕیارەکان</p>
                </div>

                {/* Blocked Customers */}
                <div className="rounded-xl border bg-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <Shield className="h-4 w-4 text-red-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-red-500">{customers.filter(c => c.isBlocked).length}</p>
                  <p className="text-xs text-muted-foreground">بلۆک کراو</p>
                </div>
                {/* Customers with Active Installments */}
                <div className="rounded-xl border bg-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <CreditCard className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">
                    {customers.filter(c => products.filter(p => p.customerId === c.id && p.remainingAmount > 0).length > 0).length}
                  </p>
                  <p className="text-xs text-muted-foreground">کڕیار قیستی لایە</p>
                </div>

                {/* Customers with Completed Installments */}
                <div className="rounded-xl border bg-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {customers.filter(c => {
                      const custProducts = products.filter(p => p.customerId === c.id);
                      return custProducts.length > 0 && custProducts.every(p => p.remainingAmount <= 0.001);
                    }).length}
                  </p>
                  <p className="text-xs text-muted-foreground">کڕیار قیستی تەواوبوو</p>
                </div>
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                      <Users className="h-5 w-5" />
                      لیستی کڕیارەکان
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {searchTerm && (
                        <p className="text-sm text-muted-foreground hidden md:block">
                          {filteredCustomers.length} ئەنجام
                        </p>
                      )}
                      <div className="relative flex-1 md:w-64">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="گەڕان..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pr-9"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-2 md:px-6">
                  <CustomerTable
                    customers={filteredCustomers}
                    products={products}
                    jobs={jobs}
                    onDelete={handleDeleteCustomer}
                    onUpdateCustomer={handleUpdateCustomer}
                    onAddProduct={handleAddProduct}
                    onDeleteProduct={handleDeleteProduct}
                    onUpdateProduct={handleUpdateProduct}
                    onNavigate={(page) => setActivePage(page)}
                    onViewProfile={handleViewProfile}
                    mode="customers"
                  />
                </CardContent>
              </Card>
            </>
          )}

          {activePage === "customer-profile" && selectedCustomerForProfile && (
            <CustomerProfilePage
              customer={selectedCustomerForProfile}
              products={products}
              onBack={handleBackFromProfile}
              onUpdateProduct={handleUpdateProduct}
            />
          )}

          {activePage === "payments" && (
            <>
              {/* Summary Cards - Horizontal Table Style for Desktop, Original for Mobile */}
              <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
                {/* IQD Card */}
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-amber-500/5 border-b">
                    <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Banknote className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="font-semibold">دینار</span>
                  </div>
                  {/* Mobile: Original vertical style */}
                  <div className="md:hidden space-y-2 text-sm p-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">فرۆشتن</span>
                      <span className="font-medium flex items-center gap-1">
                        <span dir="ltr">{totalSaleIQD.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">د.ع</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">وەرگیراو</span>
                      <span className="font-medium text-green-600 flex items-center gap-1">
                        <span dir="ltr">{totalPaidIQD.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">د.ع</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ماوە</span>
                      <span className="font-medium text-red-500 flex items-center gap-1">
                        <span dir="ltr">{totalRemainingIQD.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">د.ع</span>
                      </span>
                    </div>
                  </div>
                  {/* Desktop: Horizontal table style */}
                  <div className="hidden md:grid grid-cols-3 divide-x divide-x-reverse">
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">فرۆشتن</span>
                      <span className="font-semibold text-lg flex items-center gap-1">
                        <span dir="ltr">{totalSaleIQD.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground font-normal">د.ع</span>
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">وەرگیراو</span>
                      <span className="font-semibold text-lg text-green-600 flex items-center gap-1">
                        <span dir="ltr">{totalPaidIQD.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground font-normal">د.ع</span>
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">ماوە</span>
                      <span className="font-semibold text-lg text-red-500 flex items-center gap-1">
                        <span dir="ltr">{totalRemainingIQD.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground font-normal">د.ع</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* USD Card */}
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/5 border-b">
                    <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                    </div>
                    <span className="font-semibold">دۆلار</span>
                  </div>
                  {/* Mobile: Original vertical style */}
                  <div className="md:hidden space-y-2 text-sm p-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">فرۆشتن</span>
                      <span className="font-medium flex items-center gap-1">
                        <span dir="ltr">${totalSaleUSD.toLocaleString()}</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">وەرگیراو</span>
                      <span className="font-medium text-green-600 flex items-center gap-1">
                        <span dir="ltr">${totalPaidUSD.toLocaleString()}</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ماوە</span>
                      <span className="font-medium text-red-500 flex items-center gap-1">
                        <span dir="ltr">${totalRemainingUSD.toLocaleString()}</span>
                      </span>
                    </div>
                  </div>
                  {/* Desktop: Horizontal table style */}
                  <div className="hidden md:grid grid-cols-3 divide-x divide-x-reverse">
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">فرۆشتن</span>
                      <span className="font-semibold text-lg" dir="ltr">${totalSaleUSD.toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">وەرگیراو</span>
                      <span className="font-semibold text-lg text-green-600" dir="ltr">${totalPaidUSD.toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">ماوە</span>
                      <span className="font-semibold text-lg text-red-500" dir="ltr">${totalRemainingUSD.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    {/* Title */}
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                      <CreditCard className="h-5 w-5" />
                      لیستی پارەدانەکان
                    </CardTitle>
                    {/* Search Bar and Results */}
                    <div className="flex items-center gap-2">
                      {searchTerm && (
                        <p className="text-sm text-muted-foreground hidden md:block">
                          {filteredCustomers.length} ئەنجام
                        </p>
                      )}
                      <div className="relative flex-1 md:w-64">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="گەڕان..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pr-9"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-2 md:px-6">
                  <PaymentsTable
                    customers={filteredCustomers}
                    products={products}
                    jobs={jobs}
                    onAddProduct={handleAddProduct}
                    onDeleteProduct={handleDeleteProduct}
                    onUpdateProduct={handleUpdateProduct}
                    onNavigate={(page) => setActivePage(page)}
                    onSelectCustomer={(customer) => {
                      setPaymentsSelectedCustomer(customer);
                      setPaymentsProfileOpen(true);
                    }}
                  />
                </CardContent>
              </Card>

              {/* Customer Payment Sheet for Payments */}
              <CustomerPaymentSheet
                customer={paymentsSelectedCustomer}
                products={products}
                open={paymentsProfileOpen}
                onClose={() => setPaymentsProfileOpen(false)}
                onAddProduct={handleAddProduct}
                onDeleteProduct={handleDeleteProduct}
                onUpdateProduct={handleUpdateProduct}
                onNavigate={(page) => setActivePage(page)}
                hideAddProduct={true}
              />
            </>
          )}

          {activePage === "sales" && (
            <>
              {/* Summary Cards - Horizontal Table Style for Desktop, Original for Mobile */}
              <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
                {/* IQD Card */}
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-amber-500/5 border-b">
                    <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Banknote className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="font-semibold">دینار</span>
                  </div>
                  {/* Mobile: Original vertical style */}
                  <div className="md:hidden space-y-2 text-sm p-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">فرۆشتن</span>
                      <span className="font-medium flex items-center gap-1">
                        <span dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.salePrice, 0).toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">د.ع</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">وەرگیراو</span>
                      <span className="font-medium text-green-600 flex items-center gap-1">
                        <span dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.paidAmount, 0).toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">د.ع</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ماوە</span>
                      <span className="font-medium text-red-500 flex items-center gap-1">
                        <span dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.remainingAmount, 0).toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">د.ع</span>
                      </span>
                    </div>
                  </div>
                  {/* Desktop: Horizontal table style */}
                  <div className="hidden md:grid grid-cols-3 divide-x divide-x-reverse">
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">فرۆشتن</span>
                      <span className="font-semibold text-lg flex items-center gap-1">
                        <span dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.salePrice, 0).toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground font-normal">د.ع</span>
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">وەرگیراو</span>
                      <span className="font-semibold text-lg text-green-600 flex items-center gap-1">
                        <span dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.paidAmount, 0).toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground font-normal">د.ع</span>
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">ماوە</span>
                      <span className="font-semibold text-lg text-red-500 flex items-center gap-1">
                        <span dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.remainingAmount, 0).toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground font-normal">د.ع</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* USD Card */}
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/5 border-b">
                    <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                    </div>
                    <span className="font-semibold">دۆلار</span>
                  </div>
                  {/* Mobile: Original vertical style */}
                  <div className="md:hidden space-y-2 text-sm p-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">فرۆشتن</span>
                      <span className="font-medium flex items-center gap-1">
                        <span dir="ltr">${products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.salePrice, 0).toLocaleString()}</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">وەرگیراو</span>
                      <span className="font-medium text-green-600 flex items-center gap-1">
                        <span dir="ltr">${products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.paidAmount, 0).toLocaleString()}</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ماوە</span>
                      <span className="font-medium text-red-500 flex items-center gap-1">
                        <span dir="ltr">${products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.remainingAmount, 0).toLocaleString()}</span>
                      </span>
                    </div>
                  </div>
                  {/* Desktop: Horizontal table style */}
                  <div className="hidden md:grid grid-cols-3 divide-x divide-x-reverse">
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">فرۆشتن</span>
                      <span className="font-semibold text-lg" dir="ltr">${products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.salePrice, 0).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">وەرگیراو</span>
                      <span className="font-semibold text-lg text-green-600" dir="ltr">${products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.paidAmount, 0).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 py-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm text-muted-foreground mb-2">ماوە</span>
                      <span className="font-semibold text-lg text-red-500" dir="ltr">${products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.remainingAmount, 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                      <ShoppingCart className="h-5 w-5" />
                      لیستی فرۆشتنەکان
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {salesSearchTerm && (
                        <p className="text-sm text-muted-foreground hidden md:block">
                          {products.filter(p => {
                            const customer = customers.find(c => c.id === p.customerId);
                            const search = salesSearchTerm.toLowerCase();
                            return (
                              p.deviceName.toLowerCase().includes(search) ||
                              p.deviceDescription?.toLowerCase().includes(search) ||
                              customer?.fullName.toLowerCase().includes(search) ||
                              customer?.customerCode.toString().includes(search)
                            );
                          }).length} ئەنجام
                        </p>
                      )}
                      <div className="relative flex-1 md:w-64">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="گەڕان..."
                          value={salesSearchTerm}
                          onChange={(e) => setSalesSearchTerm(e.target.value)}
                          className="pr-9"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-2 md:px-6">
                  <Tabs defaultValue="active" className="w-full" dir="rtl">
                    <TabsList className="w-full h-auto p-1 bg-muted/50 rounded-lg border gap-1 grid grid-cols-2">
                      <TabsTrigger
                        value="active"
                        className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        <span>فرۆشراوەکان</span>
                      </TabsTrigger>
                      <TabsTrigger
                        value="completed"
                        className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>تەواوبووەکان</span>
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="active" className="mt-3">
                        {(() => {
                          const activeProducts = [...products.filter(p => {
                            if (p.remainingAmount <= 0) return false;
                            if (!salesSearchTerm) return true;
                            const customer = customers.find(c => c.id === p.customerId);
                            const search = salesSearchTerm.toLowerCase();
                            return (
                              p.deviceName.toLowerCase().includes(search) ||
                              p.deviceDescription?.toLowerCase().includes(search) ||
                              customer?.fullName.toLowerCase().includes(search) ||
                              customer?.customerCode.toString().includes(search)
                            );
                          })].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                          
                          // Pagination for active sales
                          const totalActivePages = Math.ceil(activeProducts.length / salesItemsPerPage);
                          const activeStartIndex = (activeSalesPage - 1) * salesItemsPerPage;
                          const activeEndIndex = activeStartIndex + salesItemsPerPage;
                          const paginatedActiveProducts = activeProducts.slice(activeStartIndex, activeEndIndex);
                          
                          if (activeProducts.length === 0) {
                            return (
                              <div className="text-center py-12 text-muted-foreground">
                                هیچ فرۆشتنێکی چالاک نییە
                              </div>
                            );
                          }
                          return (
                            <>
                              {/* Header with count */}
                              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                                <span className="text-sm text-muted-foreground">کۆی فرۆشتنەکان: <span className="font-medium text-foreground">{activeProducts.length}</span></span>
                              </div>
                              {/* Desktop Table */}
                              <div className="rounded-md border hidden md:block overflow-hidden shadow-sm">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-muted/50">
                                      <TableHead className="text-right w-12 font-semibold">#</TableHead>
                                      <TableHead className="text-right font-semibold">کڕیار / ئامێر</TableHead>
                                      <TableHead className="text-right font-semibold">نرخی فرۆشتن</TableHead>
                                      <TableHead className="text-right font-semibold">پارەی وەرگیراو</TableHead>
                                      <TableHead className="text-right font-semibold">پارەی ماوە</TableHead>
                                      <TableHead className="text-right font-semibold">بەرواری کڕین</TableHead>
                                      <TableHead className="text-right font-semibold max-w-[140px]">تێبینی</TableHead>
                                      <TableHead className="text-center w-[120px] font-semibold">کردارەکان</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {paginatedActiveProducts.map((product, index) => {
                                      const globalIndex = activeStartIndex + index;
                                      const customer = customers.find(c => c.id === product.customerId);
                                      return (
                                        <TableRow key={`${product.id}-${index}`} className={`transition-colors hover:bg-muted/50 ${index % 2 === 1 ? 'bg-muted/30' : ''}`}>
                                          <TableCell className="text-center font-medium text-muted-foreground">
                                            {globalIndex + 1}
                                          </TableCell>
                                          <TableCell>
                                            <div>
                                              <span className="font-semibold text-sm">{product.deviceName}</span>
                                              {product.deviceDescription && (
                                                <p className="text-xs text-muted-foreground mt-0.5">{product.deviceDescription}</p>
                                              )}
                                              <div className="mt-1">
                                                <span className="font-medium text-xs text-gray-600 dark:text-gray-400">{customer?.fullName || "-"}</span>
                                              </div>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1.5 justify-start">
                                              <span className="font-semibold" dir="ltr">{product.salePrice.toLocaleString()}</span>
                                              <Badge variant="outline" className="text-[10px] px-1.5 h-5">
                                                {product.currency === "IQD" ? "د.ع" : "$"}
                                              </Badge>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1.5 justify-start">
                                              <span className="font-semibold text-green-600" dir="ltr">{product.paidAmount.toLocaleString()}</span>
                                              <Badge variant="secondary" className="text-[10px] px-1.5 h-5">
                                                {product.currency === "IQD" ? "د.ع" : "$"}
                                              </Badge>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1.5 justify-start">
                                              <span className="font-semibold text-red-500" dir="ltr">{product.remainingAmount.toLocaleString()}</span>
                                              <Badge variant="destructive" className="text-[10px] px-1.5 h-5 text-white">
                                                {product.currency === "IQD" ? "د.ع" : "$"}
                                              </Badge>
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-muted-foreground"><span dir="ltr">{product.date}</span></TableCell>
                                          <TableCell className="max-w-[140px]" title={product.notes || "-"}>
                                            <div className="text-xs text-muted-foreground truncate">
                                              {product.notes ? (
                                                <span className="bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded border border-amber-200 dark:border-amber-900 inline-block max-w-full truncate">
                                                  {product.notes}
                                                </span>
                                              ) : (
                                                <span className="text-muted-foreground/50">-</span>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center justify-center gap-1">
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 bg-primary/10 text-primary hover:bg-primary/20"
                                                onClick={() => {
                                                  setEditingSale(product);
                                                  setEditSaleDialogOpen(true);
                                                }}
                                                title="دەستکاری"
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </Button>
                                              {(product.paidAmount > 0 || (product.installments || 0) > 0) ? (
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-8 w-8"
                                                  disabled
                                                  title="ناتوانرێت بسڕێتەوە (پارەدان کراوە)"
                                                >
                                                  <Trash2 className="h-4 w-4 text-muted-foreground/50" />
                                                </Button>
                                              ) : (
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-8 w-8 bg-destructive/10 text-destructive hover:bg-destructive/20"
                                                  onClick={async () => {
                                                    await confirm({
                                                      title: "سڕینەوەی فرۆشتن",
                                                      description: "دڵنیایت لە سڕینەوەی ئەم فرۆشتنە؟",
                                                      variant: "destructive",
                                                      confirmText: "بەڵێ، بیسڕەوە",
                                                      cancelText: "نەخێر",
                                                      onConfirm: async () => {
                                                        await handleDeleteProduct(product.id);
                                                      }
                                                    });
                                                  }}
                                                  title="سڕینەوە"
                                                >
                                                  <Trash2 className="h-4 w-4" />
                                                </Button>
                                              )}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                                
                                <Pagination
                                  currentPage={activeSalesPage}
                                  totalPages={totalActivePages}
                                  onPageChange={(page) => {
                                    setActiveSalesPage(page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  totalItems={activeProducts.length}
                                  itemsPerPage={salesItemsPerPage}
                                />
                              </div>

                              {/* Mobile Cards */}
                              <div className="md:hidden space-y-3">
                                {paginatedActiveProducts.map((product, index) => {
                                  const globalIndex = activeStartIndex + index;
                                  const customer = customers.find(c => c.id === product.customerId);
                                  const isExpanded = expandedSales.has(product.id);
                                  const isEven = index % 2 === 1;
                                  return (
                                    <div key={`${product.id}-${index}`} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                                      <div className="p-3 cursor-pointer" onClick={() => toggleSaleExpanded(product.id)}>
                                        {/* Main Row */}
                                        <div className="flex items-center gap-3">
                                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-bold text-primary">{globalIndex + 1}</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                              <Badge variant={product.currency === "IQD" ? "outline" : "secondary"} className="text-[10px] px-1.5 h-4">
                                                {product.currency === "IQD" ? "د.ع" : "$"}
                                              </Badge>
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                              {customer?.fullName || "-"}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleSaleExpanded(product.id);
                                              }}
                                            >
                                              {isExpanded ? (
                                                <ChevronUp className="h-4 w-4" />
                                              ) : (
                                                <ChevronDown className="h-4 w-4" />
                                              )}
                                            </Button>
                                          </div>
                                        </div>
                                        {/* Mini Table */}
                                        <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                                          <div className="text-center">
                                            <div className="text-muted-foreground text-[10px]">نرخ</div>
                                            <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                                          </div>
                                          <div className="text-center border-x border-dashed">
                                            <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                            <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                                          </div>
                                          <div className="text-center">
                                            <div className="text-muted-foreground text-[10px]">ماوە</div>
                                            <div className="font-semibold text-red-500" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.remainingAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.remainingAmount.toLocaleString()}</>}</div>
                                          </div>
                                        </div>
                                        {/* Notes badge if present */}
                                        {product.notes && (
                                          <div className="mt-2 text-xs">
                                            <div className="bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 rounded border border-amber-200 dark:border-amber-900 text-muted-foreground truncate">
                                              <span className="font-medium text-amber-900 dark:text-amber-100">تێبینی:</span> {product.notes}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      {isExpanded && (
                                        <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                                          <div className="space-y-2 pt-3">
                                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                              <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                              <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                              <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                              <span className="font-medium text-sm">{product.installments || 0}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                              <span className="text-muted-foreground text-xs">بەروار</span>
                                              <span className="font-medium text-sm" dir="ltr">{product.date}</span>
                                            </div>
                                            {product.deviceDescription && (
                                              <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                                <span className="text-muted-foreground text-xs">وەسف</span>
                                                <span className="font-medium text-sm">{product.deviceDescription}</span>
                                              </div>
                                            )}
                                            {/* Action buttons */}
                                            <div className="pt-2 mt-2 border-t flex gap-2">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="flex-1 bg-primary/10 text-primary hover:bg-primary/20 border-primary/20"
                                                onClick={() => {
                                                  setEditingSale(product);
                                                  setEditSaleDialogOpen(true);
                                                }}
                                              >
                                                <Pencil className="h-3.5 w-3.5 ml-1.5" />
                                                دەستکاری
                                              </Button>
                                              {(product.paidAmount > 0 || (product.installments || 0) > 0) ? (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="flex-1"
                                                  disabled
                                                >
                                                  <Trash2 className="h-3.5 w-3.5 ml-1.5 text-muted-foreground/50" />
                                                  سڕینەوە
                                                </Button>
                                              ) : (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="flex-1 bg-destructive/10 text-destructive hover:bg-destructive/20 border-destructive/20"
                                                  onClick={async () => {
                                                    await confirm({
                                                      title: "سڕینەوەی فرۆشتن",
                                                      description: "دڵنیایت لە سڕینەوەی ئەم فرۆشتنە؟",
                                                      variant: "destructive",
                                                      confirmText: "بەڵێ، بیسڕەوە",
                                                      cancelText: "نەخێر",
                                                      onConfirm: async () => {
                                                        await handleDeleteProduct(product.id);
                                                      }
                                                    });
                                                  }}
                                                >
                                                  <Trash2 className="h-3.5 w-3.5 ml-1.5" />
                                                  سڕینەوە
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              <div className="md:hidden mt-3">
                                <Pagination
                                  currentPage={activeSalesPage}
                                  totalPages={totalActivePages}
                                  onPageChange={(page) => {
                                    setActiveSalesPage(page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  totalItems={activeProducts.length}
                                  itemsPerPage={salesItemsPerPage}
                                />
                              </div>
                            </>
                          );
                        })()}
                      </TabsContent>

                      <TabsContent value="completed" className="mt-3">
                        {(() => {
                          const completedProducts = [...products.filter(p => {
                            if (p.remainingAmount > 0) return false;
                            if (!salesSearchTerm) return true;
                            const customer = customers.find(c => c.id === p.customerId);
                            const search = salesSearchTerm.toLowerCase();
                            return (
                              p.deviceName.toLowerCase().includes(search) ||
                              p.deviceDescription?.toLowerCase().includes(search) ||
                              customer?.fullName.toLowerCase().includes(search) ||
                              customer?.customerCode.toString().includes(search)
                            );
                          })].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                          
                          // Pagination for completed sales
                          const totalCompletedPages = Math.ceil(completedProducts.length / salesItemsPerPage);
                          const completedStartIndex = (completedSalesPage - 1) * salesItemsPerPage;
                          const completedEndIndex = completedStartIndex + salesItemsPerPage;
                          const paginatedCompletedProducts = completedProducts.slice(completedStartIndex, completedEndIndex);
                          
                          if (completedProducts.length === 0) {
                            return (
                              <div className="text-center py-12 text-muted-foreground">
                                هیچ فرۆشتنێکی تەواوبوو نییە
                              </div>
                            );
                          }
                          return (
                            <>
                              {/* Header with count */}
                              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                                <span className="text-sm text-muted-foreground">کۆی تەواوبووەکان: <span className="font-medium text-foreground">{completedProducts.length}</span></span>
                              </div>
                              {/* Desktop Table */}
                              <div className="rounded-md border hidden md:block overflow-hidden shadow-sm">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-muted/50">
                                      <TableHead className="text-right w-12 font-semibold">#</TableHead>
                                      <TableHead className="text-right font-semibold">کڕیار / ئامێر</TableHead>
                                      <TableHead className="text-right font-semibold">نرخی فرۆشتن</TableHead>
                                      <TableHead className="text-right font-semibold">پارەی وەرگیراو</TableHead>
                                      <TableHead className="text-right font-semibold">بەرواری کڕین</TableHead>
                                      <TableHead className="text-right font-semibold max-w-[140px]">تێبینی</TableHead>
                                      <TableHead className="text-center w-[120px] font-semibold">کردارەکان</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {paginatedCompletedProducts.map((product, index) => {
                                      const globalIndex = completedStartIndex + index;
                                      const customer = customers.find(c => c.id === product.customerId);
                                      return (
                                        <TableRow key={`${product.id}-${index}`} className={`transition-colors hover:bg-muted/50 ${index % 2 === 1 ? 'bg-muted/30' : ''}`}>
                                          <TableCell className="text-center font-medium text-muted-foreground">
                                            {globalIndex + 1}
                                          </TableCell>
                                          <TableCell>
                                            <div>
                                              <span className="font-semibold text-sm">{product.deviceName}</span>
                                              {product.deviceDescription && (
                                                <p className="text-xs text-muted-foreground mt-0.5">{product.deviceDescription}</p>
                                              )}
                                              <div className="mt-1">
                                                <span className="font-medium text-xs text-gray-600 dark:text-gray-400">{customer?.fullName || "-"}</span>
                                              </div>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1.5 justify-start">
                                              <span className="font-semibold" dir="ltr">{product.salePrice.toLocaleString()}</span>
                                              <Badge variant="outline" className="text-[10px] px-1.5 h-5">
                                                {product.currency === "IQD" ? "د.ع" : "$"}
                                              </Badge>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1.5 justify-start">
                                              <span className="font-semibold text-green-600" dir="ltr">{product.paidAmount.toLocaleString()}</span>
                                              <Badge variant="secondary" className="text-[10px] px-1.5 h-5">
                                                {product.currency === "IQD" ? "د.ع" : "$"}
                                              </Badge>
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-muted-foreground"><span dir="ltr">{product.date}</span></TableCell>
                                          <TableCell className="max-w-[140px]" title={product.notes || "-"}>
                                            <div className="text-xs text-muted-foreground truncate">
                                              {product.notes ? (
                                                <span className="bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded border border-amber-200 dark:border-amber-900 inline-block max-w-full truncate">
                                                  {product.notes}
                                                </span>
                                              ) : (
                                                <span className="text-muted-foreground/50">-</span>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center justify-center gap-1">
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 bg-primary/10 text-primary hover:bg-primary/20"
                                                onClick={() => {
                                                  setEditingSale(product);
                                                  setEditSaleDialogOpen(true);
                                                }}
                                                title="دەستکاری"
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </Button>
                                              {(product.paidAmount > 0 || (product.installments || 0) > 0) ? (
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-8 w-8"
                                                  disabled
                                                  title="ناتوانرێت بسڕێتەوە (پارەدان کراوە)"
                                                >
                                                  <Trash2 className="h-4 w-4 text-muted-foreground/50" />
                                                </Button>
                                              ) : (
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-8 w-8 bg-destructive/10 text-destructive hover:bg-destructive/20"
                                                  onClick={async () => {
                                                    await confirm({
                                                      title: "سڕینەوەی فرۆشتن",
                                                      description: "دڵنیایت لە سڕینەوەی ئەم فرۆشتنە؟",
                                                      variant: "destructive",
                                                      confirmText: "بەڵێ، بیسڕەوە",
                                                      cancelText: "نەخێر",
                                                      onConfirm: async () => {
                                                        await handleDeleteProduct(product.id);
                                                      }
                                                    });
                                                  }}
                                                  title="سڕینەوە"
                                                >
                                                  <Trash2 className="h-4 w-4" />
                                                </Button>
                                              )}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                                
                                <Pagination
                                  currentPage={completedSalesPage}
                                  totalPages={totalCompletedPages}
                                  onPageChange={(page) => {
                                    setCompletedSalesPage(page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  totalItems={completedProducts.length}
                                  itemsPerPage={salesItemsPerPage}
                                />
                              </div>

                              {/* Mobile Cards */}
                              <div className="md:hidden space-y-3">
                                {paginatedCompletedProducts.map((product, index) => {
                                  const globalIndex = completedStartIndex + index;
                                  const customer = customers.find(c => c.id === product.customerId);
                                  const isExpanded = expandedSales.has(product.id);
                                  const isEven = index % 2 === 1;
                                  return (
                                    <div key={`${product.id}-${index}`} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                                      <div className="p-3 cursor-pointer" onClick={() => toggleSaleExpanded(product.id)}>
                                        {/* Main Row */}
                                        <div className="flex items-center gap-3">
                                          <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-bold text-green-600">{globalIndex + 1}</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                              <Badge variant={product.currency === "IQD" ? "outline" : "secondary"} className="text-[10px] px-1.5 h-4">
                                                {product.currency === "IQD" ? "د.ع" : "$"}
                                              </Badge>
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                              {customer?.fullName || "-"}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleSaleExpanded(product.id);
                                              }}
                                            >
                                              {isExpanded ? (
                                                <ChevronUp className="h-4 w-4" />
                                              ) : (
                                                <ChevronDown className="h-4 w-4" />
                                              )}
                                            </Button>
                                          </div>
                                        </div>
                                        {/* Mini Table */}
                                        <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                                          <div className="text-center">
                                            <div className="text-muted-foreground text-[10px]">نرخ</div>
                                            <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                                          </div>
                                          <div className="text-center border-x border-dashed">
                                            <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                            <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                                          </div>
                                          <div className="text-center">
                                            <div className="text-muted-foreground text-[10px]">دۆخ</div>
                                            <Badge variant="default" className="text-[10px] px-1.5 h-4 bg-green-600">تەواو</Badge>
                                          </div>
                                        </div>
                                        {/* Notes badge if present */}
                                        {product.notes && (
                                          <div className="mt-2 text-xs">
                                            <div className="bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 rounded border border-amber-200 dark:border-amber-900 text-muted-foreground truncate">
                                              <span className="font-medium text-amber-900 dark:text-amber-100">تێبینی:</span> {product.notes}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      {isExpanded && (
                                        <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                                          <div className="space-y-2 pt-3">
                                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                              <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                              <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                              <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                              <span className="font-medium text-sm">{product.installments || 0}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                              <span className="text-muted-foreground text-xs">بەروار</span>
                                              <span className="font-medium text-sm" dir="ltr">{product.date}</span>
                                            </div>
                                            {product.deviceDescription && (
                                              <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                                <span className="text-muted-foreground text-xs">وەسف</span>
                                                <span className="font-medium text-sm">{product.deviceDescription}</span>
                                              </div>
                                            )}
                                            {/* Action buttons */}
                                            <div className="pt-2 mt-2 border-t flex gap-2">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="flex-1 bg-primary/10 text-primary hover:bg-primary/20 border-primary/20"
                                                onClick={() => {
                                                  setEditingSale(product);
                                                  setEditSaleDialogOpen(true);
                                                }}
                                              >
                                                <Pencil className="h-3.5 w-3.5 ml-1.5" />
                                                دەستکاری
                                              </Button>
                                              {(product.paidAmount > 0 || (product.installments || 0) > 0) ? (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="flex-1"
                                                  disabled
                                                >
                                                  <Trash2 className="h-3.5 w-3.5 ml-1.5 text-muted-foreground/50" />
                                                  سڕینەوە
                                                </Button>
                                              ) : (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="flex-1 bg-destructive/10 text-destructive hover:bg-destructive/20 border-destructive/20"
                                                  onClick={async () => {
                                                    await confirm({
                                                      title: "سڕینەوەی فرۆشتن",
                                                      description: "دڵنیایت لە سڕینەوەی ئەم فرۆشتنە؟",
                                                      variant: "destructive",
                                                      confirmText: "بەڵێ، بیسڕەوە",
                                                      cancelText: "نەخێر",
                                                      onConfirm: async () => {
                                                        await handleDeleteProduct(product.id);
                                                      }
                                                    });
                                                  }}
                                                >
                                                  <Trash2 className="h-3.5 w-3.5 ml-1.5" />
                                                  سڕینەوە
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              <div className="md:hidden mt-3">
                                <Pagination
                                  currentPage={completedSalesPage}
                                  totalPages={totalCompletedPages}
                                  onPageChange={(page) => {
                                    setCompletedSalesPage(page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  totalItems={completedProducts.length}
                                  itemsPerPage={salesItemsPerPage}
                                />
                              </div>
                            </>
                          );
                        })()}
                      </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </>
          )}

          {activePage === "expenses" && (
            <ExpensesPage
              expenses={expenses}
              onAddExpense={addExpense}
              onUpdateExpense={updateExpense}
              onDeleteExpense={deleteExpense}
              addDialogOpen={addExpenseDialogOpen}
              onAddDialogOpenChange={setAddExpenseDialogOpen}
            />
          )}

          {activePage === "reports" && (
            <ReportsPage products={products} customers={customers} />
          )}

          {activePage === "notifications" && (
            <OverdueTable
              customers={customers}
              products={products}
              paymentHistory={[]}
              onAddProduct={handleAddProduct}
              onDeleteProduct={handleDeleteProduct}
              onUpdateProduct={handleUpdateProduct}
              onNavigate={(page) => setActivePage(page)}
            />
          )}

          {activePage === "users" && (
            <UsersPage ref={usersPageRef} />
          )}

          {activePage === "settings" && (
            <SettingsPage onNavigate={(page) => setActivePage(page)} />
          )}
        </main>
      </div >

      {/* Logout Confirmation Dialog */}
      < LogoutDialog
        open={logoutDialogOpen}
        onOpenChange={setLogoutDialogOpen}
        onConfirm={onLogout}
      />

      {/* Edit Sale Dialog */}
      < EditSaleDialog
        product={editingSale}
        open={editSaleDialogOpen}
        onOpenChange={setEditSaleDialogOpen}
        onUpdate={handleUpdateProduct}
      />
    </div >
  );
}
